import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { toSentryError } from '@pps/observability';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@pps/ui/components/ui/button';
import { logger } from '@/lib/logger';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });
    const errorId = logger.errorWithId('Global error boundary captured an exception', error, {
      boundary: 'global',
      componentStack: errorInfo.componentStack,
    });
    Sentry.captureException(toSentryError(error), {
      contexts: { react: { componentStack: errorInfo.componentStack } },
      tags: { boundary: 'global', errorId },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <GlobalErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
        />
      );
    }

    return this.props.children;
  }
}

function GlobalErrorFallback({
  error,
  errorInfo,
}: {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="max-w-md w-full text-center space-y-6">
        <div className="w-16 h-16 mx-auto rounded-full bg-destructive/10 flex items-center justify-center">
          <AlertTriangle className="w-8 h-8 text-destructive" />
        </div>

        <div className="space-y-2">
          <h1 className="text-2xl font-bold text-foreground">
            Algo deu errado
          </h1>
          <p className="text-muted-foreground">
            Ocorreu um erro inesperado na aplicação. Recarregue a página para
            continuar.
          </p>
        </div>

        {import.meta.env.DEV && error && (
          <div className="bg-muted rounded-lg p-4 text-left">
            <p className="text-sm font-mono text-destructive break-all">
              {error.message}
            </p>
            {errorInfo && (
              <pre className="mt-2 text-xs text-muted-foreground overflow-auto max-h-40">
                {errorInfo.componentStack}
              </pre>
            )}
          </div>
        )}

        <Button onClick={() => window.location.reload()} className="w-full">
          <RefreshCw className="w-4 h-4 mr-2" />
          Recarregar página
        </Button>
      </div>
    </div>
  );
}

export default ErrorBoundary;
